// App.jsx
// Root component with all routes
// Phase 2: Assessment flow added

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AssessmentProvider } from './context/AssessmentContext';
import Spinner from './components/common/Spinner';

// Public pages
import LandingPage from './pages/public/LandingPage';
import CaptureForm from './pages/public/CaptureForm';
import PaymentPage from './pages/public/PaymentPage';
import AssessmentForm from './pages/public/AssessmentForm';
import FreeReport from './pages/public/FreeReport';
import ResultsPage from './pages/public/ResultsPage';
import SharedAssessment from './pages/public/SharedAssessment';
import SharedForm from './pages/public/SharedForm';

// Dashboard pages
import AdminLogin from './pages/dashboard/AdminLogin';
import DashboardHome from './pages/dashboard/DashboardHome';
import Submissions from './pages/dashboard/Submissions';
import SubmissionDetail from './pages/dashboard/SubmissionDetail';
import Transactions from './pages/dashboard/Transactions';
import Meetings from './pages/dashboard/Meetings';
import Enterprise from './pages/dashboard/Enterprise';
import CompanyDetail from './pages/dashboard/CompanyDetail';
import FormMaker from './pages/dashboard/FormMaker';
import FeedbackResponses from './pages/dashboard/FeedbackResponses';
import Analytics from './pages/dashboard/Analytics';
import Settings from './pages/dashboard/Settings';

import './styles/global.css';

// Protected route - redirects to login if not authenticated
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Spinner fullPage />;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  return children;
};

// Public route - redirects to dashboard if already authenticated
const PublicAdminRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Spinner fullPage />;
  if (isAuthenticated) return <Navigate to="/admin/dashboard" replace />;
  return children;
};

// Coming soon placeholder
const AppRoutes = () => (
  <Routes>
    {/* PUBLIC ASSESSMENT ROUTES */}
    <Route path="/" element={<LandingPage />} />
    <Route path="/f/:token" element={<SharedForm />} />
    <Route path="/s/:token" element={<SharedAssessment />} />
    <Route path="/start" element={<CaptureForm />} />
    <Route path="/payment" element={<Navigate to="/start" replace />} />
    <Route path="/assessment" element={<AssessmentForm />} />
    <Route path="/results/:submissionId" element={<ResultsPage />} />
    <Route path="/report/free/:submissionId" element={<FreeReport />} />
    {/* /upgrade was never built. It rendered a "Phase 4 coming soon" placeholder,
        and the $149 button in the free report email pointed at it, so there was no
        way to pay. The email now links to the payment CTA on the free report page.
        This route is kept rather than deleted because emails already sent contain
        /upgrade links; they land on the home page instead of a dev placeholder. */}
    <Route path="/upgrade" element={<Navigate to="/" replace />} />

    {/* ADMIN ROUTES */}
    <Route path="/admin/login" element={<PublicAdminRoute><AdminLogin /></PublicAdminRoute>} />
    <Route path="/admin/dashboard" element={<ProtectedRoute><DashboardHome /></ProtectedRoute>} />
    <Route path="/admin/submissions" element={<ProtectedRoute><Submissions /></ProtectedRoute>} />
    <Route path="/admin/submissions/:id" element={<ProtectedRoute><SubmissionDetail /></ProtectedRoute>} />
    <Route path="/admin/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
    <Route path="/admin/meetings" element={<ProtectedRoute><Meetings /></ProtectedRoute>} />
    <Route path="/admin/enterprise" element={<ProtectedRoute><Enterprise /></ProtectedRoute>} />
    <Route path="/admin/enterprise/:id" element={<ProtectedRoute><CompanyDetail /></ProtectedRoute>} />
    <Route path="/admin/forms" element={<ProtectedRoute><FormMaker /></ProtectedRoute>} />
    <Route path="/admin/forms/:formId/responses" element={<ProtectedRoute><FeedbackResponses /></ProtectedRoute>} />
    <Route path="/admin/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
    <Route path="/admin/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

    {/* Catch all */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <AssessmentProvider>
        <AppRoutes />
      </AssessmentProvider>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
